import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { SettingsUpdateBidItemRequest } from '../../../../../../../core/projects.actions';
import { settingsBidSheet } from '../../../../../../../core/projects.selectors';

@Component({
  standalone: false,
  selector: 'app-bid-items-shapes',
  templateUrl: './bid-items-shapes.component.html',
  styleUrls: ['./bid-items-shapes.component.scss'],
})
export class BidItemsShapesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  @Input() projectId: string | null = null;

  shapes: any[] = [];
  selectedShapeId: string | null = null;
  fieldOptions: { value: string; label: string }[] = [];
  fieldMap: { [key: string]: any } = {};
  isDirty = false;
  private loaded = false;
  private bidSheetId: string | null = null;
  private readonly onDestroy = new Subject<void>();

  allTypeOptions = [
    { value: 'point', label: 'Point' },
    { value: 'line', label: 'Line' },
    { value: 'shape', label: 'Shape (Polygon)' },
    { value: 'volume', label: 'Volume' },
  ];

  get availableTypeOptions() {
    const usedTypes = this.shapes
      .map(s => s.form.get('type')?.value)
      .filter(Boolean);
    return this.allTypeOptions.filter(o => !usedTypes.includes(o.value));
  }

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.store.select(settingsBidSheet).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      if (data?.id) {
        this.bidSheetId = data.id;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.buildFieldOptions();
      if (!this.loaded && this.data?.shapes) {
        this.loadExistingShapes();
        this.loaded = true;
      }
    }
  }

  private buildFieldOptions() {
    const fields = Array.isArray(this.data?.fields)
      ? this.data.fields
      : (this.data?.fields?.list ?? []);

    const flat = fields.reduce((acc: any[], item: any) => {
      if (item?.fields && Array.isArray(item.fields)) {
        return [...acc, ...item.fields];
      }
      return [...acc, item];
    }, []);

    const customOptions = flat.map((f: any) => {
      const key = f.name || f.id;
      this.fieldMap[key] = f;
      return {
        value: key,
        label: f.label || f.name || 'Unnamed field',
      };
    });

    // Always include "Quantity" as the first target option
    this.fieldOptions = [
      { value: 'quantity', label: 'Quantity' },
      ...customOptions,
    ];
    this.fieldMap['quantity'] = { name: 'quantity', label: 'Quantity', type: 'number' };
  }

  private loadExistingShapes() {
    const shapesData = this.data?.shapes;
    if (!shapesData?.types?.length) return;

    this.shapes = [];
    for (const type of shapesData.types) {
      const typeName = (type.name || '').toLowerCase();
      const mappings = type.field_mappings || {};
      const fieldKeys = Object.keys(mappings);

      const form = new FormGroup({
        type: new FormControl(typeName),
        fields: new FormControl(fieldKeys),
      });

      const fieldForms: any = {};
      for (const [targetField, sourceExpression] of Object.entries(mappings)) {
        fieldForms[targetField] = new FormGroup({
          value: new FormControl(sourceExpression),
        });
        fieldForms[targetField].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
          this.isDirty = true;
        });
      }

      const shape: any = {
        id: `shape_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        form,
        expandedFieldId: null,
        fieldForms,
        showError: false,
      };

      form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
        shape.showError = false;
        this.isDirty = true;
      });

      this.shapes.push(shape);
    }
  }

  addShape() {
    const unselected = this.shapes.find(s => !s.form.get('type')?.value);
    if (unselected) {
      unselected.showError = true;
      unselected.form.get('type').markAsTouched();
      unselected.form.get('type').setErrors({ required: true });
      this.selectedShapeId = unselected.id;
      return;
    }

    const form = new FormGroup({
      type: new FormControl(null),
      fields: new FormControl([]),
    });

    const newShape: any = {
      id: `shape_${Date.now()}`,
      form,
      expandedFieldId: null,
      fieldForms: {},
      showError: false,
    };

    form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      newShape.showError = false;
      this.isDirty = true;
    });

    this.shapes.push(newShape);
    this.isDirty = true;
    this.selectedShapeId = newShape.id;
  }

  toggleShape(shape: any) {
    this.selectedShapeId = this.selectedShapeId === shape.id ? null : shape.id;
  }

  confirmDeleteShape(shape: any, event: Event) {
    event.stopPropagation();
    const label = this.getShapeLabel(shape);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete', `Are you sure you want to delete "${label}"?`),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.shapes = this.shapes.filter(s => s.id !== shape.id);
        this.isDirty = true;
        if (this.selectedShapeId === shape.id) {
          this.selectedShapeId = null;
        }
      }
    });
  }

  getShapeLabel(shape: any): string {
    const type = shape.form.get('type')?.value;
    if (type) {
      const option = this.allTypeOptions.find(o => o.value === type);
      return option ? option.label : type;
    }
    return 'Select type';
  }

  getFieldLabel(fieldId: string): string {
    const option = this.fieldOptions.find(o => o.value === fieldId);
    return option ? option.label : fieldId;
  }

  getSourceHint(shape: any): string {
    const type = shape.form.get('type')?.value;
    if (type === 'point') return 'source_count (count)';
    if (type === 'line') return 'source_length (ft), source_count (count)';
    if (type === 'shape') return 'source_area (sq ft), source_length (ft), source_count (count)';
    if (type === 'volume') return 'source_volume (cu ft), source_area (sq ft), source_count (count)';
    return 'source_area (sq ft), source_length (ft), source_count (count)';
  }

  getSourceExample(shape: any): string {
    const type = shape.form.get('type')?.value;
    if (type === 'point') return 'source_count';
    if (type === 'line') return 'source_length';
    if (type === 'volume') return 'source_volume / 27';
    return 'source_area / 9';
  }

  toggleFieldAccordion(shape: any, fieldId: string) {
    shape.expandedFieldId = shape.expandedFieldId === fieldId ? null : fieldId;
  }

  getFieldSubForm(shape: any, fieldId: string): FormGroup {
    if (!shape.fieldForms[fieldId]) {
      shape.fieldForms[fieldId] = new FormGroup({
        value: new FormControl(null),
      });
      shape.fieldForms[fieldId].valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
        this.isDirty = true;
      });
    }
    return shape.fieldForms[fieldId];
  }

  save() {
    const types = this.shapes
      .filter(s => s.form.get('type')?.value)
      .map(s => {
        const typeName = s.form.get('type').value;
        const fields = s.form.get('fields')?.value || [];
        const fieldMappings: any = {};
        for (const fieldId of fields) {
          const subForm = s.fieldForms[fieldId];
          const value = subForm?.get('value')?.value;
          if (value != null && String(value).trim() !== '') {
            fieldMappings[fieldId] = String(value).trim();
          }
        }
        return {
          name: typeName,
          field_mappings: fieldMappings,
        };
      });

    const body = {
      item: this.data.item,
      description: this.data.description,
      uom: this.data.uom,
      quantity: this.data.quantity,
      unit_price: this.data.unit_price,
      bid_sheet_id: this.bidSheetId,
      site_id: this.data.site_id ?? null,
      rollup_field_id: this.data.rollup_field_id ?? null,
      record_by_station: this.data.record_by_station ?? false,
      track_material_quantity: this.data.track_material_quantity ?? false,
      rollup_quantity_editable: this.data.rollup_quantity_editable ?? false,
      rollup_formula: this.data.rollup_formula ?? null,
      quantity_rounding: this.data.quantity_rounding ?? null,
      shapes: { types },
    };
    this.store.dispatch(SettingsUpdateBidItemRequest({
      payload: {
        projectId: this.projectId,
        bidItemId: this.data.id,
        body,
      },
    }));
    this.isDirty = false;
    this.commonService.notification('Shapes saved', 'success');
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
